import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../api/client';
import { useUIStore } from '../../../stores/uiStore';
import {
  Rocket, Search, FileText, Eye, CheckCircle2, XCircle,
  Clock, AlertCircle, Loader2, Package, Trash2, RefreshCw,
  ChevronLeft, ChevronRight, X, Send, Sparkles, Link,
  PenTool, Grid3X3, BarChart3, Filter, Check, Ban,
  TrendingUp, Binoculars, FolderTree, Plus,
  Play, Pause, Settings2, ChevronDown, ChevronUp,
  Globe, Star, Weight, ShoppingCart, Zap, Edit3, Layers
} from 'lucide-react';
import { DiscoveryResultsTable } from '../components/DiscoveryResultsTable';
import { QueueSelectedBar } from '../components/QueueSelectedBar';
import { Can, useCan } from '../../../lib/rbac';

function CategoryBrowser({
  selectedCategory,
  onSelect,
}: {
  selectedCategory: any;
  onSelect: (cat: any) => void;
}) {
  const { t } = useTranslation();
  const { sellerId } = useUIStore();
  const [roots, setRoots] = useState<any[]>([]);
  const [children, setChildren] = useState<any[]>([]);
  const [breadcrumb, setBreadcrumb] = useState<any[]>([]);
  const [loadingCats, setLoadingCats] = useState(false);
  const [manualCatId, setManualCatId] = useState('');

  useEffect(() => {
    setLoadingCats(true);
    api.get<any>(`/publisher/discover/categories/roots?seller_id=${sellerId}`).then(res => {
      if (res.success && res.data) setRoots(Array.isArray(res.data) ? res.data : res.data.categories || []);
    }).catch(() => {}).finally(() => setLoadingCats(false));
  }, [sellerId]);

  // Navigate INTO a category (drill-down to children) — does NOT select
  const navigateCategory = async (cat: any) => {
    if (!cat.children_count || cat.children_count === 0) return; // leaf — no children to navigate into
    setLoadingCats(true);
    try {
      const res = await api.get<any>(`/publisher/discover/categories/${cat.id || cat.category_id}?seller_id=${sellerId}`);
      if (res.success && res.data) {
        const ch = Array.isArray(res.data) ? res.data : res.data.children || [];
        setChildren(ch);
        setBreadcrumb(prev => [...prev, cat]);
      }
    } catch (err) {
      console.error('Category navigate failed:', err);
    } finally {
      setLoadingCats(false);
    }
  };

  // Select a category for use in Finder/Bestsellers — does NOT navigate
  const selectCategory = (cat: any) => {
    onSelect(cat);
  };

  const goBack = (index: number) => {
    if (index < 0) {
      setBreadcrumb([]);
      setChildren([]);
      onSelect(null);
      return;
    }
    const newCrumb = breadcrumb.slice(0, index + 1);
    setBreadcrumb(newCrumb);
    api.get<any>(`/publisher/discover/categories/${newCrumb[newCrumb.length - 1].id || newCrumb[newCrumb.length - 1].category_id}?seller_id=${sellerId}`).then(res => {
      if (res.success && res.data) setChildren(Array.isArray(res.data) ? res.data : res.data.children || []);
    }).catch(() => {});
  };

  // Manual category ID input
  const handleManualCatId = () => {
    const catId = parseInt(manualCatId.trim(), 10);
    if (!catId || isNaN(catId)) return;
    onSelect({ id: catId, catId, name: `Category #${catId}` });
  };

  const displayItems = breadcrumb.length === 0 ? roots : children;
  const isSelected = (cat: any) => selectedCategory && (selectedCategory.id === (cat.id || cat.category_id) || selectedCategory.category_id === (cat.id || cat.category_id));

  return (
    <div className="bg-[var(--color-bg)] border border-[var(--color-border)] rounded-lg p-3">
      <label className="text-xs text-[var(--color-text-tertiary)] block mb-2">{t('pub.feed.rootCategory')}</label>
      {/* Manual category ID input */}
      <div className="flex items-center gap-2 mb-2">
        <input
          type="number"
          value={manualCatId}
          onChange={e => setManualCatId(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleManualCatId()}
          placeholder="Keepa Category ID"
          className="flex-1 px-2 py-1 bg-[var(--color-bg)] border border-[var(--color-border)] rounded text-xs text-[var(--color-text)] font-mono"
        />
        <button
          onClick={handleManualCatId}
          disabled={!manualCatId.trim()}
          className="px-2 py-1 bg-[var(--color-primary)] text-white rounded text-xs font-medium hover:opacity-90 disabled:opacity-50"
        >
          {t('common.select') || 'Select'}
        </button>
      </div>
      {/* Selected category indicator */}
      {selectedCategory && (
        <div className="flex items-center gap-2 mb-2 px-2 py-1 bg-[var(--color-primary)]/10 border border-[var(--color-primary)]/20 rounded text-xs">
          <Check size={12} className="text-[var(--color-primary)]" />
          <span className="text-[var(--color-primary)] font-medium">
            {selectedCategory.name || selectedCategory.category_name} <span className="font-mono opacity-70">#{selectedCategory.id || selectedCategory.category_id || selectedCategory.catId}</span>
          </span>
          <button onClick={() => onSelect(null)} className="ml-auto text-[var(--color-text-tertiary)] hover:text-[var(--color-danger)]"><X size={12} /></button>
        </div>
      )}
      {/* Breadcrumb */}
      {breadcrumb.length > 0 && (
        <div className="flex flex-wrap items-center gap-1 mb-2 text-xs">
          <button onClick={() => goBack(-1)} className="text-[var(--color-primary)] hover:underline">Root</button>
          {breadcrumb.map((c, i) => (
            <span key={i} className="flex items-center gap-1">
              <ChevronRight size={10} className="text-[var(--color-text-tertiary)]" />
              <button onClick={() => goBack(i)} className="text-[var(--color-primary)] hover:underline">
                {c.name || c.category_name || c.id}
              </button>
            </span>
          ))}
        </div>
      )}
      {/* Category list */}
      {loadingCats ? (
        <div className="flex justify-center py-4"><Loader2 size={16} className="animate-spin text-[var(--color-primary)]" /></div>
      ) : (
        <div className="max-h-48 overflow-y-auto space-y-0.5">
          {displayItems.map((cat, i) => (
            <div
              key={cat.id || cat.category_id || i}
              className={`w-full flex items-center gap-1 px-2 py-1.5 rounded text-xs transition-colors ${
                isSelected(cat)
                  ? 'bg-[var(--color-primary)]/10'
                  : 'hover:bg-[var(--color-bg-tertiary)]'
              }`}
            >
              {/* Select radio */}
              <button
                onClick={() => selectCategory(cat)}
                className={`flex-shrink-0 w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors ${
                  isSelected(cat)
                    ? 'border-[var(--color-primary)] bg-[var(--color-primary)]'
                    : 'border-[var(--color-border)] hover:border-[var(--color-primary)]'
                }`}
                title={`Select ${cat.name || cat.id}`}
              >
                {isSelected(cat) && <Check size={8} className="text-white" />}
              </button>
              {/* Category name + ID — click selects */}
              <button
                onClick={() => selectCategory(cat)}
                className={`flex-1 text-left flex items-center gap-1.5 min-w-0 ${
                  isSelected(cat) ? 'text-[var(--color-primary)]' : 'text-[var(--color-text)]'
                }`}
              >
                <FolderTree size={12} className="flex-shrink-0 text-[var(--color-text-tertiary)]" />
                <span className="truncate">{cat.name || cat.category_name || cat.id}</span>
                <span className="flex-shrink-0 font-mono text-[10px] text-[var(--color-text-tertiary)]">#{cat.id || cat.category_id}</span>
              </button>
              {/* Navigate arrow — only if has children */}
              {cat.children_count != null && cat.children_count > 0 && (
                <button
                  onClick={() => navigateCategory(cat)}
                  className="flex-shrink-0 flex items-center gap-1 text-[var(--color-text-tertiary)] hover:text-[var(--color-primary)] px-1"
                  title={`Browse ${cat.children_count} subcategories`}
                >
                  <span className="text-[10px]">{cat.children_count}</span>
                  <ChevronRight size={12} />
                </button>
              )}
            </div>
          ))}
          {displayItems.length === 0 && (
            <p className="text-center text-xs text-[var(--color-text-tertiary)] py-3">{t('pub.feed.noResults')}</p>
          )}
        </div>
      )}
    </div>
  );
}

function ScoutsSubTab({ sites = ['MLA', 'MLM', 'MLC', 'MCO'], feederFilters }: { sites?: string[]; feederFilters?: Record<string, any> }) {
  const { t } = useTranslation();
  const canScouts = useCan()('publisher.scouts');
  const { sellerId } = useUIStore();
  const [scouts, setScouts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [viewingScout, setViewingScout] = useState<any>(null);
  const [scoutResults, setScoutResults] = useState<any[]>([]);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [selectedAsins, setSelectedAsins] = useState<Set<string>>(new Set());
  const [enqueuing, setEnqueuing] = useState(false);

  // Form state
  const [form, setForm] = useState({
    name: '',
    category_id: '',
    frequency_hours: 24,
    lookback_hours: 48,
    max_results: 100,
    auto_queue: false,
  });
  const [savingScout, setSavingScout] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<any>(null);

  const loadScouts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get<any>(`/publisher/scouts?seller_id=${sellerId}`);
      if (res.success && res.data) setScouts(Array.isArray(res.data) ? res.data : res.data.scouts || []);
    } catch (err) {
      console.error('Load scouts failed:', err);
    } finally {
      setLoading(false);
    }
  }, [sellerId]);

  useEffect(() => { loadScouts(); }, [loadScouts]);

  const handleSaveScout = async () => {
    setSavingScout(true);
    try {
      // Snapshot current feeder filters — only enabled ones (basic + advanced)
      const raw = feederFilters ? { ...feederFilters } : {};
      const enabled = raw._enabled || {};
      delete raw.sites;
      delete raw.maxAsins;
      delete raw._enabled;
      // Only keep fields that are toggled ON (enabled[field] !== false)
      const snapshot = Object.fromEntries(Object.entries(raw).filter(([k]) => enabled[k] !== false));
      const cleanFilters = Object.fromEntries(Object.entries(snapshot).filter(([, v]) => v != null && v !== '' && v !== false && v !== 0 && !(Array.isArray(v) && v.length === 0)));

      await api.post('/publisher/scouts', {
        ...form,
        root_category: selectedCategory ? (selectedCategory.catId || selectedCategory.id || selectedCategory.category_id) : form.category_id,
        root_category_name: selectedCategory ? selectedCategory.name : undefined,
        seller_id: sellerId,
        filters: Object.keys(cleanFilters).length > 0 ? cleanFilters : undefined,
      });
      setShowForm(false);
      setForm({ name: '', category_id: '', frequency_hours: 24, lookback_hours: 48, max_results: 100, auto_queue: false });
      setSelectedCategory(null);
      loadScouts();
    } catch (err) {
      console.error('Save scout failed:', err);
    } finally {
      setSavingScout(false);
    }
  };

  const handleRunNow = async (scoutId: number) => {
    try {
      await api.post(`/publisher/scouts/${scoutId}/run`, { seller_id: sellerId });
      loadScouts();
    } catch (err) {
      console.error('Run scout failed:', err);
    }
  };

  const handleDelete = async (scoutId: number) => {
    try {
      await api.delete(`/publisher/scouts/${scoutId}?seller_id=${sellerId}`);
      loadScouts();
    } catch (err) {
      console.error('Delete scout failed:', err);
    }
  };

  const handleToggle = async (scout: any) => {
    try {
      await api.put(`/publisher/scouts/${scout.id}`, { active: !scout.active, seller_id: sellerId });
      loadScouts();
    } catch (err) {
      console.error('Toggle scout failed:', err);
    }
  };

  const viewResults = async (scout: any) => {
    setViewingScout(scout);
    setResultsLoading(true);
    try {
      const res = await api.get<any>(`/publisher/scouts/${scout.id}/results?seller_id=${sellerId}`);
      if (res.success && res.data) {
        const items = Array.isArray(res.data) ? res.data : res.data.items || [];
        setScoutResults(items);
      }
    } catch (err) {
      console.error('Load scout results failed:', err);
    } finally {
      setResultsLoading(false);
    }
  };

  const toggleAsin = (asin: string) => {
    setSelectedAsins(prev => {
      const next = new Set(prev);
      if (next.has(asin)) next.delete(asin); else next.add(asin);
      return next;
    });
  };

  const handleQueueResults = async () => {
    if (selectedAsins.size === 0 || !viewingScout) return;
    setEnqueuing(true);
    try {
      await api.post(`/publisher/scouts/${viewingScout.id}/results/queue`, {
        asins: Array.from(selectedAsins),
        seller_id: sellerId,
      });
      setSelectedAsins(new Set());
    } catch (err) {
      console.error('Queue scout results failed:', err);
    } finally {
      setEnqueuing(false);
    }
  };

  if (viewingScout) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => { setViewingScout(null); setScoutResults([]); setSelectedAsins(new Set()); }}
            className="p-1.5 rounded hover:bg-[var(--color-bg-tertiary)]"
          >
            <ChevronLeft size={18} />
          </button>
          <h3 className="font-medium text-[var(--color-text)]">{viewingScout.name}</h3>
          <span className="text-xs text-[var(--color-text-tertiary)]">{t('pub.feed.viewResults')}</span>
        </div>
        <DiscoveryResultsTable items={scoutResults} selectedAsins={selectedAsins} toggleAsin={toggleAsin} loading={resultsLoading} />
        <Can cap="publisher.enqueue">
          <QueueSelectedBar count={selectedAsins.size} onQueue={handleQueueResults} loading={enqueuing} />
        </Can>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-sm font-medium text-[var(--color-text)]">{t('pub.feed.scouts')}</h3>
        <Can cap="publisher.scouts">
        <button
          onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-[var(--color-primary)] text-white rounded-lg text-xs font-medium hover:opacity-90"
        >
          <Plus size={12} />
          {t('pub.feed.newScout')}
        </button>
        </Can>
      </div>

      {/* New Scout Form */}
      {showForm && canScouts && (
        <div className="bg-[var(--color-bg-secondary)] rounded-lg border border-[var(--color-border)] p-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-[var(--color-text-tertiary)] block mb-1">{t('pub.feed.scoutName')}</label>
              <input
                type="text"
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                className="w-full px-2 py-1.5 bg-[var(--color-bg)] border border-[var(--color-border)] rounded text-sm text-[var(--color-text)] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                placeholder="My Scout..."
              />
            </div>
            <div>
              <label className="text-xs text-[var(--color-text-tertiary)] block mb-1">{t('pub.feed.frequency')} (h)</label>
              <input
                type="number"
                value={form.frequency_hours}
                onChange={e => setForm(f => ({ ...f, frequency_hours: Number(e.target.value) }))}
                className="w-full px-2 py-1.5 bg-[var(--color-bg)] border border-[var(--color-border)] rounded text-sm text-[var(--color-text)] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
            </div>
            <div>
              <label className="text-xs text-[var(--color-text-tertiary)] block mb-1">{t('pub.feed.lookback')} (h)</label>
              <input
                type="number"
                value={form.lookback_hours}
                onChange={e => setForm(f => ({ ...f, lookback_hours: Number(e.target.value) }))}
                className="w-full px-2 py-1.5 bg-[var(--color-bg)] border border-[var(--color-border)] rounded text-sm text-[var(--color-text)] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
            </div>
            <div>
              <label className="text-xs text-[var(--color-text-tertiary)] block mb-1">{t('pub.feed.maxResults')}</label>
              <input
                type="number"
                value={form.max_results}
                onChange={e => setForm(f => ({ ...f, max_results: Number(e.target.value) }))}
                className="w-full px-2 py-1.5 bg-[var(--color-bg)] border border-[var(--color-border)] rounded text-sm text-[var(--color-text)] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
            </div>
          </div>
          <CategoryBrowser selectedCategory={selectedCategory} onSelect={setSelectedCategory} />
          <label className="flex items-center gap-2 text-xs text-[var(--color-text-secondary)] cursor-pointer">
            <input
              type="checkbox"
              checked={form.auto_queue}
              onChange={e => setForm(f => ({ ...f, auto_queue: e.target.checked }))}
              className="rounded border-[var(--color-border)]"
            />
            {t('pub.feed.autoQueue')}
          </label>
          <div className="flex gap-2">
            <button
              onClick={handleSaveScout}
              disabled={savingScout || !form.name}
              className="flex items-center gap-1.5 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50"
            >
              {savingScout ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
              {t('common.save')}
            </button>
            <button
              onClick={() => setShowForm(false)}
              className="px-4 py-2 border border-[var(--color-border)] rounded-lg text-sm text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-tertiary)]"
            >
              {t('common.cancel')}
            </button>
          </div>
        </div>
      )}

      {/* Scout List */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[var(--color-primary)]" /></div>
      ) : scouts.length === 0 ? (
        <div className="text-center py-12 text-[var(--color-text-tertiary)]">
          <Binoculars size={32} className="mx-auto mb-2 opacity-30" />
          <p className="text-sm">{t('pub.feed.noResults')}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {scouts.map((scout) => (
            <div key={scout.id} className="bg-[var(--color-bg-secondary)] rounded-lg border border-[var(--color-border)] p-4">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h4 className="font-medium text-[var(--color-text)] text-sm">{scout.name}</h4>
                  <p className="text-xs text-[var(--color-text-tertiary)]">{scout.root_category_name || scout.root_category || '---'}</p>
                  {scout.filters && Object.keys(scout.filters).length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {Object.entries(scout.filters).map(([key, val]) => {
                        if (val == null || val === '' || val === false || (Array.isArray(val) && val.length === 0)) return null;
                        const label = key.replace(/^adv_/, '').replace(/_/g, ' ').replace(/gte$/i, '≥').replace(/lte$/i, '≤');
                        const display = Array.isArray(val) ? val.join(', ') : typeof val === 'boolean' ? '' : String(val);
                        return (
                          <span key={key} className="px-1.5 py-0.5 bg-[var(--color-primary)]/10 text-[var(--color-primary)] rounded text-[10px]">
                            {label}{display ? `: ${display}` : ''}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
                <Can cap="publisher.scouts" fallback={
                  <span className={`p-1 rounded ${scout.active ? 'text-green-500' : 'text-[var(--color-text-tertiary)]'}`} title={scout.active ? 'Active' : 'Inactive'}>
                    {scout.active ? <Play size={14} /> : <Pause size={14} />}
                  </span>
                }>
                  <button
                    onClick={() => handleToggle(scout)}
                    className={`p-1 rounded ${scout.active ? 'text-green-500' : 'text-[var(--color-text-tertiary)]'}`}
                    title={scout.active ? 'Active' : 'Inactive'}
                  >
                    {scout.active ? <Play size={14} /> : <Pause size={14} />}
                  </button>
                </Can>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                <div>
                  <span className="text-[var(--color-text-tertiary)]">{t('pub.feed.frequency')}</span>
                  <div className="text-[var(--color-text)]">{scout.frequency_hours || scout.frequency}h</div>
                </div>
                <div>
                  <span className="text-[var(--color-text-tertiary)]">{t('pub.feed.maxResults')}</span>
                  <div className="text-[var(--color-text)]">{scout.max_results}</div>
                </div>
                <div>
                  <span className="text-[var(--color-text-tertiary)]">{t('pub.feed.lastRun')}</span>
                  <div className="text-[var(--color-text)]">{(scout.last_run_at || scout.last_run) ? new Date(scout.last_run_at || scout.last_run).toLocaleString() : '---'}</div>
                </div>
                <div>
                  <span className="text-[var(--color-text-tertiary)]">{t('pub.feed.nextRun')}</span>
                  <div className="text-[var(--color-text)]">{(scout.next_run_at || scout.next_run) ? new Date(scout.next_run_at || scout.next_run).toLocaleString() : '---'}</div>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => viewResults(scout)}
                  className="flex items-center gap-1 px-2.5 py-1 bg-[var(--color-bg-tertiary)] rounded text-xs text-[var(--color-text-secondary)] hover:text-[var(--color-text)]"
                >
                  <Eye size={12} />
                  {t('pub.feed.viewResults')}
                </button>
                <Can cap="publisher.enqueue">
                <button
                  onClick={() => handleRunNow(scout.id)}
                  className="flex items-center gap-1 px-2.5 py-1 bg-[var(--color-primary)]/10 rounded text-xs text-[var(--color-primary)] hover:bg-[var(--color-primary)]/20"
                >
                  <Play size={12} />
                  {t('pub.feed.runNow')}
                </button>
                </Can>
                <Can cap="publisher.delete">
                <button
                  onClick={() => handleDelete(scout.id)}
                  className="flex items-center gap-1 px-2.5 py-1 bg-red-50 dark:bg-red-900/20 rounded text-xs text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30 ml-auto"
                >
                  <Trash2 size={12} />
                </button>
                </Can>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export { ScoutsSubTab };
